<template>
  <d-modal size="lg" @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('purchaseEditPopup.title') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <p class="mb-0">{{ $t('purchaseEditPopup.order') }}:
        <a
          :href="purchaseBaseUrl + 'zakupy/edycja/' + additionalData.purchaseID"
          target="_blank">
          #{{ additionalData.purchaseID }}
        </a>
      </p>
      <hr>

      <p class="mb-2">
        {{ $t('purchaseEditPopup.sewingRoomSynced', { name: purchaseData.customer_name }) }}:
        <span v-if="!purchaseData.sewing_room_synchronized" class="text-danger">{{ $t('ui.no') }}</span>
        <span v-if="purchaseData.sewing_room_synchronized" class="text-success">{{ $t('ui.yes') }}</span>
      </p>
      <p
        v-if="purchaseData.sewing_room_synchronized"
        class="mb-2">
        {{ $t('purchaseEditPopup.sewingRoomSyncDate') }}: <span class="text-primary">{{ purchaseData.sewing_room_synchronized_at  }}</span><br>
      </p>
      <p
        v-if="purchaseData.sewing_room_synchronized"
        class="mb-2">
        {{ $t('purchaseEditPopup.sewingRoomStatus', { name: purchaseData.customer_name }) }}:
        <d-badge
          v-if="purchaseData.sewing_room_status"
          :theme="sewingRoomStatus[purchaseData.sewing_room_status].style"
          :outline="sewingRoomStatus[purchaseData.sewing_room_status].outline">
          {{ sewingRoomStatus[purchaseData.sewing_room_status].text[prefferedLanguage] }}
        </d-badge>
      </p>
      <hr>
      <p class="mb-2">
        {{ $t('purchaseEditPopup.subSewingRoomSynced', { name: purchaseData.sub_customer_name }) }}:
        <span v-if="!purchaseData.sub_sewing_room_synchronized" class="text-danger">{{ $t('ui.no') }}</span>
        <span v-if="purchaseData.sub_sewing_room_synchronized" class="text-success">{{ $t('ui.yes') }}</span>
      </p>
      <p
        v-if="purchaseData.sub_sewing_room_synchronized"
        class="mb-2">
        {{ $t('purchaseEditPopup.subSewingRoomSyncDate') }}: <span class="text-primary">{{ purchaseData.sub_sewing_room_synchronized_at }}</span><br>
      </p>
      <p
        v-if="purchaseData.sub_sewing_room_synchronized"
        class="mb-2">
        {{ $t('purchaseEditPopup.subSewingRoomStatus', { name: purchaseData.sub_customer_name }) }}:
        <d-badge
          v-if="purchaseData.sub_sewing_room_status"
          :theme="sewingRoomStatus[purchaseData.sub_sewing_room_status].style"
          :outline="sewingRoomStatus[purchaseData.sub_sewing_room_status].outline">
          {{ sewingRoomStatus[purchaseData.sub_sewing_room_status].text[prefferedLanguage] }}
        </d-badge>
      </p>
      <hr>
      <p>
        {{ $t('purchaseEditPopup.totalOrderedProductsAmount') }}: <strong class="text-primary">{{ additionalData.purchaseProductQuantity }} {{ $t('ui.piecesAbbr') }}</strong><br>
        {{ $t('purchaseEditPopup.totalReservedProductsAmount') }}: <strong class="text-primary">{{ additionalData.purchaseProductReservedQuantity }} {{ $t('ui.piecesAbbr') }}</strong><br>
        {{ $t('purchaseEditPopup.reservedProductsForOrderAmount') }}: <strong class="text-primary">{{ additionalData.orderProductReservedQuantity }} {{ $t('ui.piecesAbbr') }}</strong>
      </p>

      <d-form>
        <validation-observer
          ref="purchase-edit-form"
          tag="form">
          <div class="form-group">
            <label>
              {{ $t('purchaseEditPopup.newQuantity') }}:
            </label>

            <validation-provider
              rules="required|min_value:1"
              name="quantity"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                type="number"
                :min="1"
                :step="1"
                v-model="orderQuantity" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('purchaseEditPopup.newPurchaseQuantity') }}:
            </label>

            <validation-provider
              :rules="'required|min_value:' + minimumPurchaseValue"
              name="quantity"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                type="number"
                :min="1"
                :step="1"
                v-model="totalQuantity" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>

            <small
              v-if="totalQuantity < minimumPurchaseValue"
              class="text-danger">
              {{ $t('purchaseEditPopup.purchaseValueTooLow') }}
            </small>
          </div>
        </validation-observer>
      </d-form>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        :disabled="saveInProgress"
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm">
        {{ $t('ui.save') }}
      </d-button>
    </d-modal-footer>

    <div v-if="!loadedPurchaseData" class="pb-3 pt-3 text-center overlay-loading">
      {{ $t('ui.dataLoading') }}&hellip;
    </div>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
    </d-alert>
  </d-modal>
</template>

<script>
import AppConfig from './../../.env';
import FormUtils from './../../utils/FormUtils.js';
import getPrefferedLanguage from './../../utils/i18nUtils.js';
import sewingRoomStatuses from './../../data/sewing-room-status.js';

export default {
  name: 'purchase-edit-popup',
  props: [
    'additionalData',
    'orderProductID',
    'productID',
    'purchaseProductID'
  ],
  computed: {
    minimumPurchaseValue () {
      let otherOrdersPurchaseQuantityReserved = this.additionalData.purchaseProductReservedQuantity - this.additionalData.orderProductReservedQuantity;
      return parseInt(this.orderQuantity, 10) + parseInt(otherOrdersPurchaseQuantityReserved, 10);
    },
    purchaseBaseUrl () {
      return AppConfig.additionalConfig[AppConfig.useConfig].apiServerUrl;
    },
    sewingRoomStatus () {
      return sewingRoomStatuses;
    },
    prefferedLanguage () {
      return getPrefferedLanguage();
    }
  },
  data () {
    return {
      orderQuantity: 1,
      totalQuantity: 1,
      purchaseData: false,
      // flags
      saveInProgress: false,
      loadedPurchaseData: false,
      loadError: false
    };
  },
  mounted () {
    this.orderQuantity = this.additionalData.orderProductReservedQuantity;
    this.totalQuantity = this.additionalData.purchaseProductQuantity;
    this.loadPurchaseData();
  },
  methods: {
    handleClose () {
      this.$bus.$emit('purchase-edit-popup-close');
    },
    validateForm () {
      FormUtils.validate(this.$refs['purchase-edit-form'], this.saveChanges);
    },
    loadPurchaseData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/purchases/item/' + parseInt(this.additionalData.purchaseID, 10),
        loadedKey: 'loadedPurchaseData',
        outputKey: 'purchaseData',
        errorKey: 'loadError'
      });
    },
    saveChanges () {
      this.saveInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/purchases/update-product/' + parseInt(this.purchaseProductID, 10),
        formData: {
          id: parseInt(this.orderProductID, 10),
          quantity: parseInt(this.totalQuantity, 10),
          purchase_product_quantity: parseInt(this.orderQuantity, 10)
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          console.log('Purchase edit response:', response);
          if (response.success) {
            this.saveInProgress = false;

            this.$bus.$emit('purchase-edit-operation-results', {
              quantity: parseInt(this.orderQuantity, 10),
              purchaseQuantity: parseInt(this.totalQuantity, 10),
              orderProductID: parseInt(this.orderProductID, 10),
              purchaseProductID: parseInt(this.purchaseProductID, 10),
              productID: parseInt(this.productID, 10),
              newQuantityReserved: (this.additionalData.purchaseProductReservedQuantity - this.additionalData.orderProductReservedQuantity) + parseInt(this.orderQuantity, 10)
            });

            this.cancel();
          }
        },
        failAction: () => {
          this.saveInProgress = false;
        }
      });
    },
    cancel () {
      this.orderQuantity = 1;
      this.totalQuantity = 1;
      this.handleClose();
    }
  }
}
</script>
